import { z } from 'zod'
import { DefinitieId, DomeinSleutel, DossierId, RondeSleutel } from './id.js'
import { Moment } from './moment.js'
import { PodStateEntry } from './publicatie.js'

/**
 * Wat de tab Platform leest (ontwerp 8.14, 4.12, eisenboek 12.42, D-76, fase 5 van de domeinlaag): de
 * pods met hun stand, de recente berichten in de outbox en de stand van de bezorging. Alleen lezen, en
 * alleen voor Platformbeheer; de opslag geeft het alleen aan de configurator (`platformoverzicht:lezen`).
 *
 * **Metadata en geen inhoud.** Het bericht zelf (de XML in `outbox.bericht`) gaat hier nooit mee: daarin
 * staan antwoorden, e-mailadressen en de link met de code van een uitnodiging. Ook `laatste_fout` niet bij
 * een mislukte aflevering: daarin zet de bezorger het begin van het antwoord van de ontvanger
 * (`delivery.ts`), en een ontvanger die het bericht terugzegt zou zo persoonsgegevens op dit scherm zetten.
 */

/**
 * Eén pod in het overzicht: de regel van `GET /v1/podstanden` met de ronde en haar penvoerende domein
 * erbij (migratie 0046). Een eigen vorm en geen veld op `PodStateEntry`: die lezen de configurator en de
 * scripts strikt, en een veld erbij zou ze laten omvallen zolang de opslag nieuwer is dan zij.
 */
export const PodOverviewEntry = PodStateEntry.extend({
  definitieId: DefinitieId,
  ronde: RondeSleutel,
  /** Het penvoerende domein van de ronde: het domein waaronder deze pod valt. */
  domein: DomeinSleutel,
})
export type PodOverviewEntry = z.infer<typeof PodOverviewEntry>

/**
 * De stand van één bericht in de outbox, afgeleid uit wat de bezorger schrijft (`deliverer.ts`):
 *
 * - `wachtend`: nog niet afgeleverd en nog geen poging gehad;
 * - `nieuwe-poging`: minstens één poging mislukte, en er volgt er een (de bezorger geeft nooit op);
 * - `afgeleverd`: de ontvanger antwoordde met 2xx;
 * - `overgeslagen`: de bezorger stuurt het niet, met een vaste reden (een verlopen of ingetrokken
 *   uitnodiging, een bericht van vóór het XML-koppelvlak).
 *
 * Een eindtoestand "mislukt" bestaat niet: er is geen dead-letter en geen maximum aantal pogingen.
 */
export const OutboxStatus = z.enum(['wachtend', 'nieuwe-poging', 'afgeleverd', 'overgeslagen'])
export type OutboxStatus = z.infer<typeof OutboxStatus>

/**
 * Eén bericht in de outbox, zonder inhoud.
 *
 * `eventId`, `gebeurtenis` en `instantieId` als tekst en niet als het smalle schema: op het cluster staan
 * rijen van vóór migratie 0026 en 0027, waarvan de sloten `not valid` zijn, en één afwijkende rij mag
 * een overzicht niet laten omvallen.
 */
export const OutboxEntry = z.strictObject({
  eventId: z.string().min(1),
  gebeurtenis: z.string().min(1),
  dossierId: DossierId,
  instantieId: z.string().min(1),
  /** De publicatie waaronder de instantie draait, voor de koppeling met de pods. */
  publicatieId: z.string().min(1),
  /** Alleen bij een bericht dat een revisie sluit. */
  revisieId: z.string().min(1).optional(),
  moment: Moment,
  status: OutboxStatus,
  /** Hoeveel afleveringen er zijn geprobeerd, de geslaagde meegeteld. */
  pogingen: z.number().int().nonnegative(),
  afgeleverdOp: Moment.optional(),
  overgeslagenOp: Moment.optional(),
  /** Wanneer de bezorger het bij `nieuwe-poging` weer mag proberen. */
  volgendePogingOp: Moment.optional(),
  /** Waarom het is overgeslagen; een vaste tekst van de bezorger zelf, alleen bij `overgeslagen`. */
  reden: z.string().min(1).optional(),
})
export type OutboxEntry = z.infer<typeof OutboxEntry>

/** Een bladzijde uit de outbox, nieuwste eerst; `volgende` is de cursor voor de bladzijde erna, of null. */
export const OutboxPage = z.strictObject({
  gebeurtenissen: z.array(OutboxEntry),
  volgende: z.string().min(1).nullable(),
})
export type OutboxPage = z.infer<typeof OutboxPage>

/** Hoeveel berichten een bladzijde hoogstens telt, en standaard. */
export const OUTBOX_PAGE_MAX = 200
export const OUTBOX_PAGE_DEFAULT = 50

/** De vraag om een bladzijde: hoeveel, vanaf welke cursor, en eventueel op één stand. */
export const OutboxQuery = z.strictObject({
  aantal: z.coerce.number().int().min(1).max(OUTBOX_PAGE_MAX).optional(),
  na: z.string().min(1).max(200).optional(),
  status: OutboxStatus.optional(),
})
export type OutboxQuery = z.infer<typeof OutboxQuery>

/**
 * De stand van de bezorging in één oogopslag. Een moment ontbreekt als er niets is om het over te hebben.
 */
export const DeliverySummary = z.strictObject({
  wachtend: z.number().int().nonnegative(),
  nieuwePoging: z.number().int().nonnegative(),
  afgeleverd: z.number().int().nonnegative(),
  overgeslagen: z.number().int().nonnegative(),
  /** Het moment van het oudste bericht dat nog niet is afgeleverd of overgeslagen. */
  oudsteOpenOp: Moment.optional(),
  /** Wanneer de laatste aflevering slaagde. */
  laatsteAfleveringOp: Moment.optional(),
  /** De eerstvolgende geplande nieuwe poging. */
  volgendePogingOp: Moment.optional(),
  /** Het hoogste aantal pogingen onder de open berichten: hoe lang een storing al duurt. */
  meestePogingen: z.number().int().nonnegative(),
})
export type DeliverySummary = z.infer<typeof DeliverySummary>
